import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Upload, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ProjectNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preClient = sp.get("client_id");
  const [clients, setClients] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggestClient, setSuggestClient] = useState(null); // {name, ...} extracted from PDF
  const [creatingClient, setCreatingClient] = useState(false);
  const [form, setForm] = useState({
    title: "",
    client_id: preClient || "",
    type: "pa",
    kind: "gara_pa",
    description: "",
    importo_lavori: 0,
    fee_percentage: 6.5,
    parcella: null,
    deadline: "",
    cig: "", cup: "", stazione_appaltante: "", tipo_gara: "",
    computo: 0, preventivo: 0,
    disciplinare_filename: "",
    extracted_data: null,
    milestones: [],
  });

  const [projectKinds, setProjectKinds] = useState([
    { key: "gara_pa", label: "Gara PA" },
    { key: "gara_privata", label: "Gara privata" },
    { key: "incarico_privato", label: "Incarico privato" },
    { key: "consulenza", label: "Consulenza continuativa" },
    { key: "pratica_edilizia", label: "Pratica edilizia" },
    { key: "perizia_ctu", label: "Perizia / CTU" },
    { key: "pef", label: "PEF" },
    { key: "pfte", label: "PFTE" },
    { key: "diagnosi_energetica", label: "Diagnosi energetica" },
  ]);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  // When user changes project type, filter clients list contextually
  const filteredClients = clients.filter((c) => c.type === form.type);
  const selectedClient = clients.find((c) => c.id === form.client_id);
  useEffect(() => {
    // If the currently selected client doesn't match the new type, clear it
    if (selectedClient && selectedClient.type !== form.type) {
      setForm((f) => ({ ...f, client_id: "" }));
    }
  }, [form.type]);

  const createClientFromSuggestion = async () => {
    if (!suggestClient) return;
    setCreatingClient(true);
    try {
      const norm = (s) => (s || "").toString().trim().replace(/\s+/g, " ").toLowerCase();
      // Server-side race: re-fetch clients and double-check to avoid duplicates
      const freshR = await api.get("/clients");
      const fresh = freshR.data;
      setClients(fresh);
      const dup = fresh.find((c) =>
        norm(c.name) === norm(suggestClient.name) ||
        (suggestClient.codice_ipa && c.codice_ipa && c.codice_ipa.trim() === suggestClient.codice_ipa.trim())
      );
      if (dup) {
        setForm((f) => ({ ...f, client_id: dup.id }));
        setSuggestClient(null);
        toast.info(`Cliente già presente: ${dup.name}`);
        return;
      }
      const r = await api.post("/clients", suggestClient);
      const created = r.data;
      setClients((prev) => [created, ...prev]);
      setForm((f) => ({ ...f, client_id: created.id }));
      setSuggestClient(null);
      toast.success(`Cliente creato: ${created.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione cliente"); }
    finally { setCreatingClient(false); }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/projects/extract-disciplinare`, {
        method: "POST",
        headers: { Authorization: `Bearer ${t}` },
        body: fd,
      });
      if (!r.ok) throw new Error("Errore estrazione");
      const data = await r.json();
      setExtracted(data.extracted);
      const ex = data.extracted || {};
      setForm((f) => ({
        ...f,
        title: f.title || ex.oggetto_gara || "",
        stazione_appaltante: ex.stazione_appaltante || f.stazione_appaltante,
        cig: ex.cig || f.cig,
        cup: ex.cup || f.cup,
        tipo_gara: ex.tipo_gara || f.tipo_gara,
        importo_lavori: Number(ex.importo_lavori) || f.importo_lavori,
        parcella: ex.spese_tecniche_esplicite && ex.spese_tecniche ? Number(ex.spese_tecniche) : f.parcella,
        deadline: ex.termine_presentazione_offerte || f.deadline,
        disciplinare_filename: data.filename,
        extracted_data: ex,
        description: ex.oggetto_gara || f.description,
      }));
      toast.success("Dati estratti automaticamente");

      // Check if extracted stazione_appaltante matches an existing client; if not, propose creation
      const enteName = (ex.stazione_appaltante || "").trim().replace(/\s+/g, " ");
      const enteIpa = (ex.codice_ipa || "").trim();
      const norm = (s) => (s || "").toString().trim().replace(/\s+/g, " ").toLowerCase();
      if (enteName) {
        const match = clients.find((c) =>
          norm(c.name) === norm(enteName) ||
          (enteIpa && c.codice_ipa && c.codice_ipa.trim() === enteIpa)
        );
        if (match) {
          setForm((f) => ({ ...f, client_id: match.id }));
          toast.info(`Cliente già in anagrafica: ${match.name}`);
        } else {
          setSuggestClient({
            type: "pa",
            name: enteName,
            city: ex.citta || "",
            email: ex.email_pec || "",
            address: ex.indirizzo || "",
            codice_ipa: ex.codice_ipa || "",
            rup: ex.rup || "",
          });
        }
      }
    } catch (e) {
      toast.error("Estrazione fallita");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Seleziona un cliente");
    setSaving(true);
    try {
      const payload = { ...form };
      // Auto milestones for PA if none
      if (payload.type === "pa" && payload.milestones.length === 0) {
        payload.milestones = [
          { name: "Manifestazione d'interesse", percentage: 20, due_date: null, completed: false },
          { name: "Consegna offerta / progetto", percentage: 60, due_date: payload.deadline || null, completed: false },
          { name: "Aggiudicazione", percentage: 20, due_date: null, completed: false },
        ];
      }
      const r = await api.post("/projects", payload);
      toast.success("Gara creata");
      nav(`/projects/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const parcella = form.parcella != null && form.parcella !== "" ? Number(form.parcella) : Number(form.importo_lavori || 0) * Number(form.fee_percentage || 6.5) / 100;

  return (
    <div className="p-8 lg:p-10 max-w-5xl space-y-6 fade-up">
      <button onClick={() => nav(-1)} className="mono-label flex items-center gap-1.5 hover:text-neutral-900" data-testid="back-btn">
        <ArrowLeft className="h-3 w-3" /> Indietro
      </button>
      <div>
        <div className="mono-label">Nuova pratica</div>
        <h1 className="font-display text-4xl font-medium mt-1">Crea nuova gara / progetto</h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Type selector */}
        <div className="border border-neutral-200 bg-white p-6">
          <div className="mono-label mb-3">01 · Tipo progetto</div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "pa", title: "Gara Pubblica Amm.", desc: "Disciplinare, CIG/CUP, milestone di SAL, scadenze normate" },
              { v: "private", title: "Progetto Privato", desc: "Computo, preventivo, cliente privato" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                data-testid={`project-type-${opt.v}`}
                onClick={() => setForm({ ...form, type: opt.v, kind: opt.v === "pa" ? "gara_pa" : "incarico_privato" })}
                className={`text-left p-4 border transition-all ${form.type === opt.v ? "brand-border brand-bg text-white" : "border-neutral-300 hover:border-neutral-500"}`}
              >
                <div className="font-display text-lg font-medium">{opt.title}</div>
                <div className={`text-xs mt-1 ${form.type === opt.v ? "text-white/80" : "text-neutral-500"}`}>{opt.desc}</div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <label className="mono-label">Tipologia specifica</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="project-kind"
              className="w-full border border-neutral-300 px-3 py-2 text-sm bg-white mt-1"
            >
              {projectKinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </div>
        </div>

        {/* Client */}
        <div className="border border-neutral-200 bg-white p-6">
          <div className="mono-label mb-3">02 · Cliente</div>
          <select
            required
            value={form.client_id}
            data-testid="select-client"
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            className="w-full border border-neutral-300 px-3 py-2 text-sm bg-white"
          >
            <option value="">Seleziona {form.type === "pa" ? "una Pubblica Amministrazione" : "un cliente privato"}...</option>
            {filteredClients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {filteredClients.length === 0 && (
            <p className="mono-label mt-2">Nessun {form.type === "pa" ? "ente" : "cliente privato"}. Crea un cliente prima o carica un disciplinare qui sotto.</p>
          )}
        </div>

        {/* Upload */}
        {form.type === "pa" && (
          <div className="border border-neutral-200 bg-white p-6">
            <div className="mono-label mb-3">03 · Estrazione automatica disciplinare</div>
            <label className="block border-2 border-dashed border-neutral-300 hover:border-[color:var(--brand-primary)] p-10 text-center cursor-pointer bg-neutral-50" data-testid="pdf-drop">
              <input type="file" accept="application/pdf" onChange={uploadPdf} className="hidden" data-testid="pdf-input" />
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-neutral-700">
                  <Loader2 className="h-5 w-5 animate-spin" /> Analisi Gemini in corso...
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-neutral-400" />
                  <div className="mono-label mt-3">Trascina o clicca per caricare il PDF</div>
                  <div className="text-xs text-neutral-500 mt-1">Estrazione automatica di ente, importo, scadenze, CIG, CUP...</div>
                </>
              )}
            </label>

            {/* Suggested new client from extraction */}
            {suggestClient && (
              <div className="mt-4 border-2 border-orange-300 bg-orange-50 p-4 fade-up" data-testid="new-client-suggestion">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 flex items-center justify-center bg-orange-500 text-white flex-shrink-0">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base font-medium text-orange-900">Cliente non presente in anagrafica</div>
                    <div className="text-sm text-orange-800 mt-1">
                      Vuoi creare automaticamente il cliente <strong>"{suggestClient.name}"</strong> con i dati estratti dal disciplinare?
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-orange-800/80">
                      {suggestClient.city && <div><span className="mono-label text-orange-700">Città:</span> {suggestClient.city}</div>}
                      {suggestClient.codice_ipa && <div><span className="mono-label text-orange-700">IPA:</span> {suggestClient.codice_ipa}</div>}
                      {suggestClient.rup && <div><span className="mono-label text-orange-700">RUP:</span> {suggestClient.rup}</div>}
                      {suggestClient.email && <div><span className="mono-label text-orange-700">PEC:</span> {suggestClient.email}</div>}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Button type="button" size="sm" onClick={createClientFromSuggestion} disabled={creatingClient} data-testid="create-suggested-client" className="brand-bg hover:opacity-90">
                        {creatingClient ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Crea cliente"}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setSuggestClient(null)} data-testid="dismiss-suggestion">Ignora</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {extracted && (
              <div className="mt-4 border border-emerald-200 bg-emerald-50 p-4 fade-up">
                <div className="flex items-center gap-2 text-emerald-700 mb-2">
                  <Sparkles className="h-4 w-4" /> <span className="mono-label text-emerald-700">Dati estratti da Gemini 3.1 Pro</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(extracted).filter(([k]) => !k.startsWith("_")).slice(0, 12).map(([k, v]) => (
                    <div key={k} className="border-l-2 border-emerald-300 pl-2">
                      <div className="mono-label">{k}</div>
                      <div className="text-neutral-800 truncate">{v == null ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Details */}
        <div className="border border-neutral-200 bg-white p-6 space-y-4">
          <div className="mono-label">04 · Dati generali</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Titolo *</Label><Input required data-testid="input-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            {form.type === "pa" && (
              <>
                <div><Label>Stazione appaltante</Label><Input value={form.stazione_appaltante} onChange={(e) => setForm({ ...form, stazione_appaltante: e.target.value })} /></div>
                <div><Label>Tipo gara</Label><Input value={form.tipo_gara} onChange={(e) => setForm({ ...form, tipo_gara: e.target.value })} /></div>
                <div><Label>CIG</Label><Input value={form.cig} onChange={(e) => setForm({ ...form, cig: e.target.value })} /></div>
                <div><Label>CUP</Label><Input value={form.cup} onChange={(e) => setForm({ ...form, cup: e.target.value })} /></div>
              </>
            )}
            <div><Label>Scadenza principale</Label><Input type="datetime-local" data-testid="input-deadline" value={form.deadline ? form.deadline.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
          </div>
        </div>

        {/* Economics */}
        <div className="border border-neutral-200 bg-white p-6 space-y-4">
          <div className="mono-label">05 · Valori economici</div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Importo lavori (€)</Label><Input type="number" step="0.01" data-testid="input-importo" value={form.importo_lavori} onChange={(e) => setForm({ ...form, importo_lavori: e.target.value })} /></div>
            <div><Label>Aliquota spese tecniche (%)</Label><Input type="number" step="0.01" value={form.fee_percentage} onChange={(e) => setForm({ ...form, fee_percentage: e.target.value })} /></div>
            <div><Label>Parcella (opz., sovrascrive)</Label><Input type="number" step="0.01" placeholder="calcolata" value={form.parcella ?? ""} onChange={(e) => setForm({ ...form, parcella: e.target.value === "" ? null : e.target.value })} /></div>
          </div>
          <div className="pt-3 border-t border-neutral-200 flex items-center justify-between">
            <div className="mono-label">Parcella calcolata</div>
            <div className="font-mono text-2xl font-semibold brand-text">
              € {parcella.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          {form.type === "private" && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div><Label>Computo</Label><Input type="number" step="0.01" value={form.computo} onChange={(e) => setForm({ ...form, computo: e.target.value })} /></div>
              <div><Label>Preventivo</Label><Input type="number" step="0.01" value={form.preventivo} onChange={(e) => setForm({ ...form, preventivo: e.target.value })} /></div>
            </div>
          )}
        </div>

        <Button type="submit" data-testid="submit-project" disabled={saving} className="brand-bg hover:opacity-90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea gara"}
        </Button>
      </form>
    </div>
  );
}
